/**
 * Herkenning van een DODE sessie — een opgeslagen sessie waar de server niet meer
 * achter staat.
 *
 * AANLEIDING — 07-08-2026, echte productiestoring. Een gebruiker kreeg tweefactor
 * toegewezen; bij de MFA-upgrade trekt Supabase het oude token in (`token_revoked`,
 * volkomen normaal). Zijn browser hield dat ingetrokken token vast. `getSession()`
 * leest uit localStorage en vraagt de server niets, dus de app dacht dat hij was
 * ingelogd, terwijl elke echte aanroep strandde op `session_not_found`. Resultaat:
 * het scherm "Kon accountstatus niet laden" ZONDER uitweg — geen uitlogknop, geen
 * weg terug. Incognito werkte wel, want dat begon schoon. Dat is geen oplossing die
 * je een klant kunt aanbieden.
 *
 * DE SCHERPE RAND VAN DIT PROBLEEM: dezelfde middag viel de Auth-service van
 * Supabase drie keer even niet te bereiken ("failed to connect to host=localhost").
 * Wie bij ELKE fout uitlogt, gooit bij zo'n hapering iedereen eruit. Deze module
 * zegt daarom alleen "dood" bij een expliciet, door de server benoemd sessieprobleem
 * en NOOIT bij een netwerkfout, een timeout of een 5xx. Bij twijfel: sessie behouden.
 */

/**
 * Foutcodes waarmee de server zegt: deze sessie of dit token bestaat niet meer.
 * Uitsluitend codes die een DEFINITIEF oordeel over de sessie zijn.
 */
const DODE_SESSIE_CODES = new Set([
  "session_not_found",
  "session_expired",
  "refresh_token_not_found",
  "refresh_token_already_used",
  "refresh_token_revoked",
  "invalid_refresh_token",
  "bad_jwt",
  "no_authorization",
  "user_not_found",
  "user_banned",
  // PostgREST: JWT verlopen of onbruikbaar
  "PGRST301",
  "PGRST302",
]);

/**
 * Berichten die dezelfde boodschap dragen maar zonder code binnenkomen.
 * Bewust letterlijk en eng gehouden — geen losse woorden als "invalid" of "token",
 * want die komen ook voor in fouten die niets met de sessie te maken hebben.
 */
const DODE_SESSIE_TEKSTEN = [
  "session from session_id claim in jwt does not exist",
  "invalid refresh token",
  "refresh token not found",
  "jwt expired",
  "session not found",
  // GEMETEN, niet aangenomen (live proef 07-08-2026): na een server-side intrekking
  // geeft `getUser()` GEEN code terug maar exact deze tekst, met status 400. Zonder
  // deze regel bleef juist het geval waarvoor deze module bestaat onherkend.
  // Veilig omdat de aanroeper hem alleen raadpleegt wanneer er een OPGESLAGEN sessie
  // is: "geen sessie" betekent dan dat de opgeslagen sessie onbruikbaar is.
  "auth session missing",
];

/** Signalen dat er iets met het TRANSPORT mis was — dan weten we niets over de sessie. */
const TRANSPORTFOUTEN = [
  "failed to fetch",
  "networkerror",
  "network request failed",
  "load failed",
  "timeout",
  "context canceled",
  "aborted",
  "unhandled server error",
];

function leesTekst(bron: unknown): string {
  if (typeof bron === "string") return bron.toLowerCase();
  return "";
}

function leesVeld(error: Record<string, unknown>, naam: string): string {
  const waarde = error[naam];
  return typeof waarde === "string" ? waarde : "";
}

/**
 * `true` alleen wanneer de server ondubbelzinnig zegt dat de sessie niet meer bestaat.
 *
 * Geeft `false` bij netwerkfouten, timeouts en 5xx — die zeggen niets over de sessie,
 * en uitloggen zou daar een storing van de leverancier vertalen naar dataverlies voor
 * de gebruiker.
 */
export function isDeadSessionError(error: unknown): boolean {
  if (error === null || typeof error !== "object") {
    return false;
  }
  const fout = error as Record<string, unknown>;

  const status = typeof fout.status === "number" ? fout.status : null;
  // Een serverfout is geen sessie-oordeel. Hier stoppen, vóór elke andere toets.
  if (status !== null && status >= 500) {
    return false;
  }

  const boodschap = leesTekst(fout.message);
  if (TRANSPORTFOUTEN.some((t) => boodschap.includes(t))) {
    return false;
  }

  const code = leesVeld(fout, "code") || leesVeld(fout, "error_code") || leesVeld(fout, "errorCode");
  if (code && DODE_SESSIE_CODES.has(code)) {
    return true;
  }

  if (DODE_SESSIE_TEKSTEN.some((t) => boodschap.includes(t))) {
    return true;
  }

  return false;
}
